// @file Surface posts store
import { trackEvent } from '@@/bits/analytics'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { isPostPublished } from '@@/bits/post_state'
import { getPostsByGroupByProperty } from '@@/bits/surface_posts_grouping_helper'
import { selfHarmHelplineUrl } from '@@/bits/url'
import { unboundedWatchEffect } from '@@/bits/vue'
import { SnackbarNotificationType } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { getNewUnpinSortIndex, getTopSortIndex } from '@@/surface/sorter'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import { isEmpty } from '@@/vuexstore/helpers/post'
import { SELF_HARM_CONTENT_ALERT } from 'bits/confirmation_dialog'
import { postFilterResults } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import { defineStore } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import { filterPosts, useSurfaceFilterStore } from 'pinia/surface_filter'
import { useSurfacePostSearchStore } from 'pinia/surface_post_search'
import { useSurfaceWishArrangementStore } from 'pinia/surface_wish_arrangement'
import PadletApi from 'surface/padlet_api'
import type { Cid, Id, Post, PostAttributes, UpdatePostArgs } from 'types'
import type { ComputedRef } from 'vue'
import { computed, ref } from 'vue'
import type { RootState } from 'vuexstore/surface/types'

export const useSurfacePostsStore = defineStore('surfacePosts', () => {
  const surfaceVuexStore = getVuexStore<RootState>()

  const surfaceStore = useSurfaceStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfaceFilterStore = useSurfaceFilterStore()
  const surfacePostSearchStore = useSurfacePostSearchStore()
  const { setSnackbar } = useGlobalSnackbarStore()
  const { sortPosts } = usePostSorter()

  /*
   * DRAGGING POSTS
   */
  /*
   * POST COUNT GETTERS
   */

  const currentPostsCount = computed(() => currentSortedPosts.value.length)

  /*
   * POST COLLECTION GETTERS
   */

  const allPosts = computed<Post[]>(() => surfaceVuexStore?.getters['post/savedPostsArray'] ?? [])
  const allSortedPosts = computed<Post[]>(() => sortPosts(allPosts.value))
  const sortedPostCids = computed(() => allSortedPosts.value.map((p) => p.cid) ?? [])
  const currentSortedPosts = computed(() => sortPosts(currentPosts.value))

  /*
   * POST MAP GETTERS
   */

  const allSortedPostsByGroup: ComputedRef<{
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }> = computed(() => {
    const sortedGroupIds = surfaceWishArrangementStore.sortedGroups.map((group) => group.id)
    return getPostsByGroupByProperty(allSortedPosts.value, sortedGroupIds, surfaceWishArrangementStore.wishGroupBy)
  })
  // Map of group ID to posts. Order of groups not guaranteed.
  const postsByGroup: ComputedRef<{
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }> = computed(() => {
    const sortedGroupIds = surfaceWishArrangementStore.sortedGroups.map((group) => group.id)
    return getPostsByGroupByProperty(currentSortedPosts.value, sortedGroupIds, surfaceWishArrangementStore.wishGroupBy)
  })
  // Posts by groups, but in order by which groups are sorted.
  const postsBySortedGroups: ComputedRef<Post[][]> = computed(() => {
    return surfaceWishArrangementStore.sortedGroups
      .map((group) => postsByGroup.value[group.id])
      .filter((group) => group != null) as Post[][]
  })

  const postsBySectionId = computed<{ [sectionId: number]: Post[] }>(
    () => surfaceVuexStore?.getters['post/postsBySectionId'] ?? {},
  )
  const sortedPostCidsBySectionId = computed<Record<string, Cid[]>>(() => {
    if (surfaceStore.isSectioned) {
      const sortedPostCidsBySectionId = {}
      sortedPostCids.value.forEach((postCid) => {
        const post = postEntitiesByCid.value[postCid]
        if (post.wall_section_id == null || isEmpty(post)) return
        if (sortedPostCidsBySectionId[post.wall_section_id] == null) {
          sortedPostCidsBySectionId[post.wall_section_id] = []
        }
        sortedPostCidsBySectionId[post.wall_section_id].push(postCid)
      })
      return sortedPostCidsBySectionId
    }
    return {}
  })

  /*
   * OTHER POST GETTERS
   */

  const postEntitiesByCid = computed<{ [postCid: string]: Post }>(
    () => surfaceVuexStore?.getters['post/postEntitiesByCid'] ?? {},
  )
  const savedPostEntitiesByCid = computed<{ [postCid: string]: Post }>(
    () => surfaceVuexStore?.getters['post/savedPostEntitiesByCid'] ?? {},
  )
  const postEntitiesById = computed<{ [postId: string]: Post }>(
    () => surfaceVuexStore?.getters['post/postEntitiesById'] ?? {},
  )
  const postEntities = computed<Post[]>(() => Object.values(postEntitiesByCid.value))
  const postCids = computed<string[]>(() => surfaceVuexStore?.getters['post/postCids'] ?? [])
  const newestPostCid = computed(() => surfaceVuexStore?.getters['post/newestPostCid'] ?? [])

  const hasFetchedPosts = computed<boolean>(() => surfaceVuexStore?.getters['post/isInitialPaginatedDataLoadDone'])

  const postBeingEditedCid = computed<Cid | null>(() => surfaceVuexStore?.state.post.postBeingEditedCid ?? null)
  const postBeingEdited = computed<Post | null>(() =>
    postBeingEditedCid.value != null ? savedPostEntitiesByCid.value[postBeingEditedCid.value] : null,
  )

  function isExistingPost(cid: Cid): boolean {
    return savedPostEntitiesByCid.value[cid] != null
  }

  function canPinPost(post: Post): boolean {
    return (
      surfacePermissionsStore.canIModerate && // only editor+ can pin
      isPostPublished(post) && // only published posts can be pinned
      surfaceVuexStore?.getters.isTimelineV1 === false // legacy format not supported
    )
  }

  /*
   * POST SEARCH
   */

  const currentPosts = ref<Post[]>(allPosts.value)

  void unboundedWatchEffect(() => {
    const filteredPosts = filterPosts(allPosts.value, surfaceFilterStore.filters)
    void surfacePostSearchStore
      .filterPostsBySearchTerm(filteredPosts, surfacePostSearchStore.searchTerm)
      .then((results) => {
        currentPosts.value = results
        postMessage(postFilterResults(results.length))
      })
  })
  /*
   * POST ACTIONS
   */

  function startNewPost({
    attributes,
    wishOrder,
  }: {
    attributes?: PostAttributes
    wishOrder?: { previousCid: Cid; nextCid: Cid }
  }): void {
    void surfaceVuexStore?.dispatch('post/startNewPost', { attributes, wishOrder })
  }

  function sortPost(payload: {
    postCid: Cid
    previousCid?: Cid | null
    nextCid?: Cid | null
    sortIndex?: number | null
    sectionId?: number | null
    gapSize?: number | null
  }): void {
    void surfaceVuexStore?.dispatch('post/sortedPost', payload)
  }

  function savePost(payload: { attributes: Post | PostAttributes; updatePostArgs?: UpdatePostArgs }): void {
    void surfaceVuexStore?.dispatch('post/savePost', payload, { root: true })
  }

  function shouldDisplayPost(postCid: Cid): boolean {
    // For future reference, this function used to look like:
    // return currentSortedPostsCids.value.includes(postCid)
    //
    // But we ran into a bug where there are duplicate posts
    // in the padlet if `wishSortBy` is `shuffle`. This
    // `shouldDisplayPost` function called in the `v-show`
    // directive somehow causes a second render that passes
    // duplicate posts to the `SurfacePost` component.
    // We still don't know why, but it's better to avoid
    // referencing "sorted" post getters in this function.

    return currentPosts.value.some((p) => p.cid === postCid)
  }

  async function postPollVote({
    wishId,
    pollId,
    pollChoiceIds,
  }: {
    wishId: Id
    pollId: Id
    pollChoiceIds: number[]
  }): Promise<void> {
    try {
      const updatedWish = await PadletApi.Poll.vote({ wishId, pollId, pollChoiceIds })
      await surfaceVuexStore?.dispatch('post/updatePostInStore', updatedWish, { root: true })
    } catch (error) {
      await setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Something went wrong. Please try again later.'),
      })
    }
  }

  async function rejectPost(id?: Id): Promise<void> {
    if (id == null) return

    void surfaceVuexStore?.dispatch('post/removePost', { id })

    await PadletApi.Wish.reject({ wishId: id })
  }

  function showSelfHarmPostAlert({ wish_id }: { wish_id?: Id }): void {
    if (wish_id == null) return

    trackEvent('SurfaceSelfHarmWarning', 'Showed Self-harm dialog for post', wish_id)
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        ...SELF_HARM_CONTENT_ALERT,
        afterConfirmActions: [() => navigateTo(selfHarmHelplineUrl(), { target: 'blank' })],
      },
      {
        root: true,
      },
    )
  }

  // pin/unpin posts
  function pinPost(postCid: Cid): void {
    void savePost({
      attributes: {
        cid: postCid,
        pinned_at: new Date().toISOString(),
        // We already sort pinned posts to the top but to avoid any issues
        // when dropping a post to between a pinned and an unpinned post
        // we also set the sort_index.
        // For pinning a post, we set it to the top value.
        sort_index: getTopSortIndex(),
      },
    })
  }

  function unpinPost(postCid: Cid): void {
    const { sortIndex, gapSize } = getNewUnpinSortIndex()
    void savePost({
      attributes: {
        cid: postCid,
        pinned_at: null,
        // We already sort pinned posts to the top but to avoid any issues
        // when dropping a post to between a pinned and an unpinned post
        // we also set the sort_index.
        // For unpinning a post, we set it to the value between the last
        // pinned post and the first unpinned post.
        sort_index: sortIndex,
      },
      updatePostArgs: {
        gap_size: gapSize,
      },
    })
  }

  return {
    // POST COUNT GETTERS
    currentPostsCount,

    // POST COLLECTION GETTERS
    allSortedPosts, // all posts, sorted
    sortedPostCids,
    currentPosts: computed(() => currentPosts.value), // filtered posts, not sorted. exported as computed so it can't be mutated.
    currentSortedPosts, // filtered posts, sorted

    // POST MAP GETTERS
    allSortedPostsByGroup, // map of all posts by group, sorted
    postsByGroup, // map of filtered posts by group, sorted
    postsBySortedGroups, // 2D array of filtered posts, sorted
    postsBySectionId, // map of posts by section ID
    sortedPostCidsBySectionId, // map of post CIDs by section ID, sorted

    // OTHER POST GETTERS
    postEntitiesByCid,
    savedPostEntitiesByCid,
    postEntitiesById,
    postEntities,
    postCids,
    newestPostCid,
    hasFetchedPosts,
    postBeingEditedCid,
    postBeingEdited,
    isExistingPost,
    canPinPost,

    // POST ACTIONS
    startNewPost,
    sortPost,
    shouldDisplayPost,
    postPollVote,
    savePost,
    rejectPost,
    showSelfHarmPostAlert,
    pinPost,
    unpinPost,
  }
})
